import React, { PureComponent } from 'react';
import {
  BundleJson,
  CourierJson,
  Instruction,
  InstructionType,
} from '../../types/bundleTypes';
import { DirectionsMap, DirectionsMapMarker } from './DirectionsMap';
import { Order, OrderMonitor } from '../../types/orderTypes';
import { createGroupColors } from '../../pages/oldPlanner/planner-map/map/PlannerMapMapUtils';
import dayjs from 'dayjs';
import { formatWindowInterval } from '../../pages/oldPlanner/planner/dateUtils';
import { DeviationSummary } from '../../pages/order/journey/deviations/fetchDeviations';

interface Props {
  orderInstructions: OrderInstruction[];
  height?: number | string;
  width?: number | string;
  courier?: CourierJson;
  durationCallback?: (duration: number) => void;
  distanceCallback?: (distance: number) => void;
  animate: boolean;
  refreshZoomPosition?: boolean;
  deviationSummaries: Map<string, DeviationSummary[] | undefined>;
}

export interface OrderInstruction {
  instruction: Instruction;
  order: Order | OrderMonitor;
  label: string;
  listNumber?: string;
}

export function toOrderInstructions(
  bundle: BundleJson,
  orders: (Order | OrderMonitor)[]
): OrderInstruction[] {
  const orderLookup: Map<string, Order | OrderMonitor> = orders.reduce(
    (map, order) => {
      return map.set(order.id, order);
    },
    new Map()
  );
  let number = 0;
  let prevAddress = null;
  return bundle.instructions.map(instruction => {
    const order = orderLookup.get(instruction.orderId);
    const pickupOriginAddress = order?.originAddress?.fullAddressString;
    const isPickupInstruction = instruction.type === InstructionType.Pickup;
    const isSameAddress = prevAddress && prevAddress === pickupOriginAddress;
    const shouldGroupWithPrevious = isSameAddress && isPickupInstruction;

    // TODO: Coordinate this with changing of the mobile app, to avoid mismatch between numbers in the app vs. dispatch
    // Remove "true ||" to re-enable the feature.
    if (true || !shouldGroupWithPrevious) {
      // Only increment the number when the address is different and not a pickup, to group multiple deliveries at the same location.
      number++;
    }
    const label = isPickupInstruction ? `P${number}` : `${number}`;
    prevAddress = pickupOriginAddress;

    return { instruction, order, label };
  });
}

export default class BundleMap extends PureComponent<Props> {
  static defaultProps = {
    durationCallback: () => {},
    distanceCallback: () => {},
    animate: false,
    refreshZoomPosition: true,
  };

  render() {
    const groups = this.props.orderInstructions.map(
      orderInstruction =>
        `${dayjs(orderInstruction.order.deliverFrom).format('HH:mm')} - ${dayjs(
          orderInstruction.order.deliverUntil
        ).format('HH:mm')}`
    );
    this.props.orderInstructions.forEach(orderInstruction => {
      if (
        orderInstruction.instruction.completed &&
        !groups.includes('Delivered')
      ) {
        groups.push('Delivered');
      }
      if (
        (orderInstruction.order.openCourierDeviation ||
          orderInstruction.order.deviations.length) &&
        !groups.includes('Deviation')
      ) {
        groups.push('Deviation');
      }
    });
    const windowColors = createGroupColors(groups);
    windowColors.set('Delivered', 'green');
    windowColors.set('Deviation', 'yellow');
    const orderLocations: DirectionsMapMarker[] =
      this.props.orderInstructions.map((orderInstruction, index) => {
        const { instruction, label, order } = orderInstruction;
        const { type, completed, orderId } = instruction;
        const { openCourierDeviation, deviations, originAddress } = order;
        const listNumber = index + 1;
        let lat = order.recipientLatitude;
        let lng = order.recipientLongitude;
        let address = order.recipientAddress;
        let phone = order.recipientPhone;
        let name = order.recipientName;
        let backgroundColor: string;
        let color: string;

        if (type === InstructionType.Pickup) {
          lat = originAddress.location.latitude;
          lng = originAddress.location.longitude;
          address = originAddress.fullAddressString;
          phone = order.pickupPhone;
          name = order.pickupName;
        }
        if (lat === null || lng === null) return null;

        const hasOpenDeviation = this.props.deviationSummaries
          .get(order.id)
          ?.some(it => it.isOpen);

        if (openCourierDeviation || deviations.length || hasOpenDeviation) {
          backgroundColor = 'yellow';
          color = 'black';
        } else if (completed) {
          backgroundColor = 'green';
          color = 'white';
        }

        return {
          lat,
          lng,
          renderDirection: true,
          icon: {
            duration: dayjs(order.deliverUntil).diff(order.deliverFrom, 'hour'),
            fillColor: windowColors.get(formatWindowInterval(order)),
            color,
            backgroundColor,
            label,
            info: { type, orderId, listNumber, address, name, phone },
          },
        };
      });

    if (this.props.courier) {
      const courier = this.props.courier;
      const { latitude, longitude, firstName, lastName, locationUpdatedAt } =
        courier;
      const courierName = lastName ? `${firstName} ${lastName}` : firstName;

      if (latitude != null && longitude != null) {
        orderLocations.push({
          lat: latitude,
          lng: longitude,
          icon: {
            type: 'porterbuddy',
            info: { type: 'courier', courierName, locationUpdatedAt },
          },
        });
      }
    }

    return (
      <DirectionsMap
        height={this.props.height || 500}
        width={this.props.width || '100%'}
        markers={orderLocations}
        duration={this.props.durationCallback}
        distance={this.props.distanceCallback}
        animate={this.props.animate}
        refreshZoomPosition={this.props.refreshZoomPosition}
        groups={groups}
        windowColors={windowColors}
      />
    );
  }
}
